import React, { useState, useEffect } from "react";

interface StatCardProps {
  icon: React.ReactNode;
  accentColor: string;
  title: string;
  value: number;
  description?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  accentColor,
  title,
  value,
  description,
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = value;
    const duration = 500;
    const incrementTime = 16;
    const step = Math.max(1, (end - start) / (duration / incrementTime));

    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        clearInterval(timer);
        start = end;
      }
      setDisplayValue(Math.floor(start));
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <div
      className="group relative p-6 rounded-2xl bg-white dark:bg-gray-800/50 border border-gray-200/60 dark:border-gray-700/60 shadow-sm hover:shadow-xl hover:shadow-[#5932EA]/5 dark:hover:shadow-[#5932EA]/10 dark:hover:border-[#5932EA]/20 transition-all duration-300 overflow-hidden backdrop-blur-sm"
    >
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `linear-gradient(135deg, ${accentColor}08 0%, transparent 100%)`,
        }}
      />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">
            {title}
          </p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight tabular-nums">
            {displayValue}
          </p>
          {description && (
            <p className="text-xs text-[#5932EA] mt-2 font-medium">{description}</p>
          )}
        </div>
        <div
          className="flex items-center justify-center w-12 h-12 rounded-xl shrink-0 transition-transform duration-300 group-hover:scale-110"
          style={{ backgroundColor: `${accentColor}18`, color: accentColor }}
        >
          {icon}
        </div>
      </div>
    </div>
  );
};

export default StatCard;
